import { FC, useCallback, useEffect, useState } from "react"
import { FaDatabase, FaPlus, FaSearch } from "react-icons/fa"
import { toast } from "react-toastify"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { TablePagination } from "components/paginations/TablePagination"

import { TournamentService } from "services/tournaments/tournament.service"

import { RulePreset } from "types/tournament.types"

import { PresetCard } from "./PresetCard"
import { PresetCreateForm } from "./PresetCreateForm"

export const TournamentPresets: FC = () => {
  const [presets, setPresets] = useState<RulePreset[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState("")
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<RulePreset | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [seeding, setSeeding] = useState(false)

  const loadPresets = useCallback(async () => {
    setLoading(true)
    try {
      const result = await TournamentService.listPresetsPaginated(page, 10, search || undefined)
      if (result) {
        setPresets(Array.isArray(result.data) ? result.data : [])
        setTotalPages(result.total_pages || 1)
      } else {
        setPresets([])
        setTotalPages(1)
      }
    } catch {
      // toast is handled by the service
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    loadPresets()
  }, [loadPresets])

  const handleDeleteConfirm = async (preset: RulePreset) => {
    try {
      await TournamentService.deletePreset(preset.id)
      toast.success("Preset deleted!")
      if (expandedId === preset.id) setExpandedId(null)
      setPage(1)
      loadPresets()
    } catch {
      // toast handled in service
    }
  }

  const handleSeedDefaults = async () => {
    setSeeding(true)
    try {
      const result = await TournamentService.seedDefaultPresets()
      if (result) {
        if (result.created > 0) {
          toast.success(`Seeded ${result.created} default preset(s): ${result.names.join(", ")}`)
          setPage(1)
          loadPresets()
        } else {
          toast.info("All default presets already exist")
        }
      }
    } catch {
      // toast handled in service
    } finally {
      setSeeding(false)
    }
  }

  const handleCreated = () => {
    setShowCreate(false)
    setPage(1)
    loadPresets()
  }

  return (
    <div className="space-y-6">
      {/* Header & Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage reusable point rule presets. Apply them when creating or editing tournaments.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!showCreate && (
            <>
              <button
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="flex items-center gap-2 px-4 py-2 border border-[rgba(139,92,246,0.4)] text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-sm disabled:opacity-50"
              >
                <FaDatabase className={seeding ? "animate-pulse" : ""} />
                {seeding ? "Seeding..." : "Seed Default Presets"}
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[rgba(139,92,246,1)] text-white rounded-lg hover:bg-[rgba(139,92,246,0.8)] transition-all text-sm"
              >
                <FaPlus /> New Preset
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="w-full pl-9 pr-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all text-sm"
          placeholder="Search presets..."
        />
      </div>

      {/* Create Form */}
      {showCreate && <PresetCreateForm onCreated={handleCreated} onCancel={() => setShowCreate(false)} />}

      {presets.length === 0 && !showCreate && !loading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p className="text-lg mb-2">{search ? "No presets match your search" : "No presets yet"}</p>
          <p className="text-sm">
            {search ? "Try a different search term." : "Create your first preset to reuse point rules across tournaments."}
          </p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <span className="loading loading-spinner loading-lg" />
        </div>
      )}

      {presets.map((preset) => (
        <PresetCard
          key={preset.id}
          preset={preset}
          expanded={expandedId === preset.id}
          onToggle={() => setExpandedId(expandedId === preset.id ? null : preset.id)}
          onDelete={() => setDeleteTarget(preset)}
          onUpdated={loadPresets}
        />
      ))}

      {/* Pagination */}
      {totalPages > 1 && <TablePagination page={page} totalPages={totalPages} onJump={(p) => setPage(p)} />}

      {/* Delete Confirmation Modal */}
      <ModalConfirmAction
        data={deleteTarget}
        handler={() => setDeleteTarget(null)}
        text={`Are you sure you want to delete the preset "${deleteTarget?.name}"? This cannot be undone.`}
        actionText="Delete Preset"
        actionHandler={handleDeleteConfirm}
      />
    </div>
  )
}
